import { View, Text, styled } from 'tamagui'
import { ArrowLeft, User, Shield, Palette, Mic, Bell, Keyboard, Zap } from '@tamagui/lucide-icons'
import { useAppStore } from '@/store'
import type { UserSettingsTab } from '@/store'
import { ProBadge } from '../../common'
import { users } from '@/mocks'

const Nav = styled(View, {
  width: 220,
  backgroundColor: '$sidebar',
  borderRightWidth: 1,
  borderRightColor: '$borderDefault',
  flexDirection: 'column',
  flexShrink: 0,
})

const NavHeader = styled(View, {
  height: 48,
  paddingHorizontal: 16,
  flexDirection: 'row',
  alignItems: 'center',
  gap: 8,
  borderBottomWidth: 1,
  borderBottomColor: '$borderDefault',
  cursor: 'pointer',
  hoverStyle: { backgroundColor: '$surfaceRaised' },
  // @ts-expect-error -- web transition
  style: { transition: 'background-color 150ms ease' },
})

const HeaderText = styled(Text, {
  fontFamily: '$fontHeading',
  fontWeight: '600',
  fontSize: 14,
  color: '$sidebarMuted',
})

const SectionLabel = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 11,
  fontWeight: '600',
  color: '$color8',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  paddingHorizontal: 12,
  paddingTop: 16,
  paddingBottom: 4,
})

const NavItem = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  height: 32,
  paddingHorizontal: 12,
  marginHorizontal: 8,
  borderRadius: '$borderRadiusMd',
  gap: 8,
  cursor: 'pointer',
  hoverStyle: { backgroundColor: '$sidebarHover' },
  // @ts-expect-error -- web transition
  style: { transition: 'background-color 150ms ease' },
  variants: {
    active: { true: { backgroundColor: '$sidebarActive' } },
  } as const,
})

const NavLabel = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 14,
})

const PrivacyBadge = styled(View, {
  backgroundColor: '$accent',
  borderRadius: '$borderRadiusSm',
  paddingHorizontal: 5,
  paddingVertical: 1,
  marginLeft: 'auto',
})

const tabs: { id: UserSettingsTab; label: string; icon: typeof User }[] = [
  { id: 'account', label: 'Account', icon: User },
  { id: 'privacy', label: 'Privacy & Security', icon: Shield },
  { id: 'appearance', label: 'Appearance', icon: Palette },
  { id: 'voice-video', label: 'Voice & Video', icon: Mic },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'keybinds', label: 'Keybinds', icon: Keyboard },
  { id: 'subscription', label: 'Subscription', icon: Zap },
]

export function UserSettingsNav() {
  const { userSettingsTab, setUserSettingsTab, activeServerId, navigateToServer, navigateHome } = useAppStore()

  const handleBack = () => {
    if (activeServerId) {
      navigateToServer(activeServerId)
    } else {
      navigateHome()
    }
  }

  return (
    <Nav>
      <NavHeader onPress={handleBack}>
        <ArrowLeft size={16} color="$sidebarMuted" />
        <HeaderText>Back</HeaderText>
      </NavHeader>

      <SectionLabel>User Settings</SectionLabel>
      {tabs.map((tab) => {
        const Icon = tab.icon
        const isPrivacy = tab.id === 'privacy'
        const isSubscription = tab.id === 'subscription'
        const currentUser = users['u1']
        return (
          <NavItem
            key={tab.id}
            active={userSettingsTab === tab.id}
            onPress={() => setUserSettingsTab(tab.id)}
          >
            <Icon size={16} color={userSettingsTab === tab.id ? '$accent' : '$sidebarMuted'} />
            <NavLabel color={userSettingsTab === tab.id ? '$surfaceForeground' : '$sidebarMuted'}>
              {tab.label}
            </NavLabel>
            {isPrivacy && (
              <PrivacyBadge>
                <Text fontFamily="$fontBody" fontSize={9} fontWeight="700" color="$surface">NEW</Text>
              </PrivacyBadge>
            )}
            {isSubscription && currentUser.isPro && (
              <View marginLeft="auto">
                <ProBadge size="sm" />
              </View>
            )}
          </NavItem>
        )
      })}
    </Nav>
  )
}
